package com.neurobs.js.view

import com.neurobs.util.PlainDate
import com.neurobs.errors._

import scala.concurrent.Future
import scala.concurrent.ExecutionContext.Implicits.global
import scala.util.{Failure, Success, Try}

trait ViewElement

trait CheckBox extends ViewElement {
	def checked: Boolean
	def setChecked(checked: Boolean): Unit
	def setChangeHandler(handler: () => Unit): Unit
	def setReadOnly(ro: Boolean): Unit
}

trait Radio extends ViewElement {
	def checked: Boolean
	def setChecked(checked: Boolean): Unit
	def setSelectHandler(handler: () => Unit): Unit
	def setReadOnly(ro: Boolean): Unit
	def name: String
	def setName(name: String): Unit
}

trait TextBox extends ViewElement {
	def value(stripWhitespace: Boolean = true): String = if (stripWhitespace) exactValue.trim else exactValue
	def optionValue(stripWhitespace: Boolean = true): Option[String] = {
		val v = value(stripWhitespace)
		if (v.isEmpty) None else Some(v)
	}
	def intValue(condition: Int => Boolean = _ => true): Option[Int] = Try(value().toInt).toOption.flatMap(v => if (condition(v)) Some(v) else None)
	protected def exactValue: String
	def setValue(value: String): Unit
	def setReadOnly(ro: Boolean): Unit
	def setDisabled(d: Boolean): Unit
	def clear(): Unit = setValue("")
}

trait Button extends ViewElement {
	def setEnabled(enabled: Boolean): Unit
	def setHandler(handler: () => Unit): Unit
	def setVisibility(visible: Boolean): Unit
	def setDisplay(display: Boolean): Unit
	def setCaption(c: String): Unit
}

trait Text extends ViewElement {
	def value: String
	def setValue(value: String): Unit
}

trait Link extends ViewElement {
	def setHandler(handler: () => Unit): Link
	def setActive(active: Boolean): Unit
	def setHref(url: String): Unit
	def setText(t: String): Unit
	def getText(): String
}

trait SelectBox extends ViewElement {
	def value: Option[String]
	def selectedIndex: Option[Int]
	def selected: Boolean = selectedIndex.isDefined
	def setSelected(i: Option[Int]): Unit
	def setOptions(options: Seq[String], selection: Option[Int] = None): Unit
	def setChangeHandler(handler: () => Unit): Unit
	def setSize(size: Int): Unit

	def setOptionsValue(options: Seq[String], selection: Option[String] = None): Unit = {
		setOptions(options, selection.map(options.indexOf(_)))
	}
}

class ValueSelectBox[T, V](val box: SelectBox, toCaption: T => String, toValue: T => V, noneEntry: Boolean = true) {
	private var items: Seq[T] = Seq()

	def getItems: Seq[T] = items

	def setItems(i: Seq[T]): Unit = {
		items = i
		val captions = i.map(toCaption)
		box.setOptions(if (noneEntry) Seq("[None]") ++ captions else captions)
	}

	def setItems(i: Future[Result[Seq[T]]]): Unit = {
		i.map {
			case Right(list) => setItems(list)
			case _ =>
		}
	}

	def setSelectedValue(v: Option[V]): Unit = v match {
		case None => box.setSelected(if (noneEntry) Some(0) else None)
		case Some(value) =>
			val i = items.indexWhere(toValue(_) == value) + (if (noneEntry) 1 else 0)
			box.setSelected(if (i < 0) None else Some(i))
	}

	def value: Option[V] = box.selectedIndex.flatMap(i => items.lift(if (noneEntry) i - 1 else i).map(toValue))
}

object ValueSelectBox {
	def apply[T, V](box: SelectBox, items: Seq[T], toCaption: T => String, toValue: T => V, noneEntry: Boolean): ValueSelectBox[T, V] = {
		val res = new ValueSelectBox[T, V](box, toCaption, toValue, noneEntry)
		res.setItems(items)
		res
	}
	def apply[T, V](box: SelectBox, items: Future[Result[Seq[T]]], toCaption: T => String, toValue: T => V, noneEntry: Boolean): ValueSelectBox[T, V] = {
		val res = new ValueSelectBox[T, V](box, toCaption, toValue, noneEntry)
		res.setItems(items)
		res
	}
	def yesNo(box: SelectBox): ValueSelectBox[Boolean, Boolean] = {
		apply[Boolean, Boolean](box, items = Seq[Boolean](false, true), (b: Boolean) => if (b) "Yes" else "No", (b: Boolean) => b, noneEntry = true)
	}
}



trait Container extends ViewElement {
	def clear(): Unit
	def addChild(child: Container): Unit
	def removeChild(child: Container): Unit
}

trait FileSelectBox extends ViewElement {
	def value: Option[org.scalajs.dom.raw.File]
}

object ViewElement {
	/** Gets date from box in format "YYYY-MM-DD"
	 *
	 * @param b The value is taken from here, stripping l/t whitespace.
	 * @param name Name of the date for the error message
	 * @return Error message or date
	 */
	def getDate(b: TextBox, name: String): Either[String, PlainDate] = {
		Try(java.time.LocalDate.parse(b.value())) match {
			case Failure(_) => Left(s"The $name must be a date in format YYYY-MM-DD.")
			case Success(d) => Right(PlainDate(d))
		}
	}

	/** Gets date from box in format "YYYY-MM-DD", or None if empty
	 *
	 * @param b The value is taken from here, stripping l/t whitespace.
	 * @param name Name of the date for the error message
	 * @return Error message or date
	 */
	def getOptionalDate(b: TextBox, name: String): Either[String, Option[PlainDate]] = {
		if (b.value().isEmpty) Right(None)
		else getDate(b, name).map(Some(_))
	}

	/** Gets a non-empty value from a text box
	 *
	 * @param b The value is taken from here, stripping l/t whitepsace
	 * @param name Name of the value for the error message
	 * @return Error or value
	 */
	def getNonEmpty(b: TextBox, name: String): Either[String, String] = {
		val v = b.value()
		if (v.isEmpty) Left(s"The $name is required.")
		else Right(v)
	}

	/** Gets text box value or None if box is empty or whitespace
	 *
	 * @param b The value is taken from here, stripping l/t whitepsace
	 * @return None if empty or whitespace, or Some(value)
	 */
	def getStringOption(b: TextBox): Option[String] = {
		val v = b.value()
		if (v.isEmpty) None else Some(v)
	}
}